/**
 * Trang quản lý tài khoản admin — chỉ dành cho super admin.
 *
 * Bố cục hai cột:
 * - Cột trái: danh sách admin hiện có, tìm kiếm client-side, reset mật khẩu.
 * - Cột phải: form tạo admin mới (AdminUsersCreatePanel).
 */
import { useMemo, useState } from 'react';
import { AdminUserCard } from '../../components/admin/AdminUserCard';
import { AdminUserInlineListSkeleton } from '../../components/admin/AdminUserInlineListSkeleton';
import { AdminUsersCreatePanel } from '../../components/admin/AdminUsersCreatePanel';
import { AdminUsersListToolbar } from '../../components/admin/AdminUsersListToolbar';
import { AdminUsersSkeleton } from '../../components/admin/AdminUsersSkeleton';
import { EmptyState } from '../../components/Common/EmptyState';
import { ErrorMessage } from '../../components/Common/ErrorMessage';
import { useAdminUsers } from '../../features/admin/users/hooks/useAdminUsers';
import { filterBySearchText } from '../../shared/utils/filterBySearchText';
import { formatText, TEXT } from '../../shared/languages';

/** Các trường dùng cho tìm kiếm admin trong danh sách */
const ADMIN_SEARCH_FIELDS = ['name', 'email', 'phone'];

/** Trang quản lý danh sách tài khoản admin và tạo admin mới */
export const AdminUsersPage = () => {
  const {
    adminList,
    adminForm,
    resetPasswordByAdminId,
    isLoadingAdmins,
    isCreatingAdmin,
    resettingAdminId,
    errorMessage,
    phoneError,
    successMessage,
    updateAdminFormField,
    updateResetPassword,
    createAdmin,
    resetAdminPassword,
  } = useAdminUsers();
  const [searchInput, setSearchInput] = useState('');
  const users = TEXT.admin.users;
  const common = TEXT.common;

  /** Lọc danh sách admin theo từ khóa tìm kiếm (client-side) */
  const filteredAdminList = useMemo(
    () => filterBySearchText(adminList, searchInput, ADMIN_SEARCH_FIELDS),
    [adminList, searchInput]
  );

  const summaryLabel = useMemo(
    () => formatText(users.resultsCount, { count: adminList.length }),
    [adminList.length, users.resultsCount]
  );

  const hasSearch = Boolean(searchInput.trim());

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    await createAdmin();
  };

  if (isLoadingAdmins && !adminList.length) {
    return <AdminUsersSkeleton />;
  }

  return (
    <div className="admin-users-page">
      <div className="admin-heading admin-users-heading">
        <div className="admin-users-heading-copy">
          <span className="eyebrow">{users.eyebrow}</span>
          <h1>{users.title}</h1>
          <p className="admin-users-subtitle">{summaryLabel}</p>
        </div>
      </div>

      {errorMessage && <ErrorMessage message={errorMessage} />}
      {successMessage && <div className="success-box admin-users-success">{successMessage}</div>}

      <div className="admin-users-layout">
        <section className="admin-users-main">
          <AdminUsersListToolbar
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            placeholder={users.searchPlaceholder}
            listTitle={users.listTitle}
          />

          {isLoadingAdmins ? (
            <AdminUserInlineListSkeleton />
          ) : filteredAdminList.length ? (
            <div className="admin-user-list">
              {filteredAdminList.map((adminUser) => (
                <AdminUserCard
                  key={adminUser.id}
                  adminUser={adminUser}
                  users={users}
                  common={common}
                  resetPassword={resetPasswordByAdminId[adminUser.id] || ''}
                  isResetting={resettingAdminId === adminUser.id}
                  onResetPasswordChange={(password) => updateResetPassword(adminUser.id, password)}
                  onResetPassword={() => resetAdminPassword(adminUser.id)}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title={hasSearch ? users.noResultsTitle : users.emptyTitle}
              message={hasSearch ? common.searchNoResultsMessage : users.emptyMessage}
            />
          )}
        </section>

        <AdminUsersCreatePanel
          users={users}
          common={common}
          adminForm={adminForm}
          phoneError={phoneError}
          isCreatingAdmin={isCreatingAdmin}
          onFieldChange={updateAdminFormField}
          onSubmit={handleCreateAdmin}
        />
      </div>
    </div>
  );
};
